import { FractionalMoney as FractionalMoneyPB } from "gen/proto/money/models_pb"
import { UUID as PBUUID } from "gen/proto/uuid/models_pb"
import {
  CostAdjustment as CostAdjustment,
  CostAdjustment_DistributionMethod as DistributionMethod,
  OrderShipment_Product as OrderShipmentProduct,
} from "gen/txn/models_pb"
import { Decimal } from "shared-ts/classes/decimal"
import {
  AllocationStrategies,
  FractionalMoney,
  Money,
} from "shared-ts/classes/money"
import { UUID } from "shared-ts/classes/uuid"

// DistributedCost is this line's share of a shipment-level CostAdjustment.
// The name is display-only; the backend only persists unitFee, keyed by
// costAdjustmentId (see OrderShipment.Product.distributed_costs).
export interface DistributedCost {
  name: string
  unitFee: FractionalMoney
  costAdjustmentId: PBUUID | undefined
}

// calculateDistributedCostsByProductId splits each distributed
// CostAdjustment's amount across products, merged per product id.
export function calculateDistributedCostsByProductId(
  costAdjustments: CostAdjustment[],
  products: OrderShipmentProduct[],
  costsById: Record<string, FractionalMoneyPB>
): Record<string, DistributedCost[]> {
  const distributedCostsByProductId: Record<string, DistributedCost[]> = {}

  for (const costAdjustment of costAdjustments) {
    const amount = Money.fromPB(costAdjustment.amount)
    const sharesByProductId = (() => {
      switch (costAdjustment.distributionMethod) {
        case DistributionMethod.BY_QUANTITY:
          return distributeByQuantity(
            costAdjustment.name,
            costAdjustment.id,
            amount,
            products
          )
        case DistributionMethod.BY_SUBTOTAL:
          return distributeBySubtotal(
            costAdjustment.name,
            costAdjustment.id,
            amount,
            products,
            costsById
          )
        default:
          return {}
      }
    })()

    for (const productId in sharesByProductId) {
      distributedCostsByProductId[productId] ??= []
      distributedCostsByProductId[productId].push(sharesByProductId[productId])
    }
  }

  return distributedCostsByProductId
}

function distributeByQuantity(
  name: string,
  costAdjustmentId: PBUUID | undefined,
  amount: Money,
  products: OrderShipmentProduct[]
): Record<string, DistributedCost> {
  const quantities = products.map((product) =>
    Decimal.fromPBOrZero(product.quantityReceivedV2)
  )
  const totalQuantity = quantities.reduce(
    (sum, quantity) => sum.add(quantity),
    Decimal.zero()
  )
  if (totalQuantity.isZero()) {
    return {}
  }

  const lineShares = amount.allocate(
    quantities,
    AllocationStrategies.LargestRemainder
  )

  const shares: Record<string, DistributedCost> = {}
  products.forEach((product, i) => {
    const id = product.id && UUID.fromPB(product.id).toString()
    const quantity = quantities[i]
    if (!id || quantity.isZero()) return
    shares[id] = {
      name,
      unitFee: FractionalMoney.fromMoney(lineShares[i]).div(quantity),
      costAdjustmentId,
    }
  })
  return shares
}

function distributeBySubtotal(
  name: string,
  costAdjustmentId: PBUUID | undefined,
  amount: Money,
  products: OrderShipmentProduct[],
  costsById: Record<string, FractionalMoneyPB>
): Record<string, DistributedCost> {
  const quantities = products.map((product) =>
    Decimal.fromPBOrZero(product.quantityReceivedV2)
  )
  // Subtotals stay unrounded so the weights match the server's allocation.
  const subtotals = products.map((product, i) => {
    const id = product.id && UUID.fromPB(product.id).toString()
    if (!id) return FractionalMoney.zero()
    const cost = FractionalMoney.fromPB(costsById[id])
    return cost.mult(quantities[i])
  })
  const totalSubtotal = subtotals.reduce(
    (sum, subtotal) => sum.add(subtotal),
    FractionalMoney.zero()
  )
  if (totalSubtotal.eq(FractionalMoney.zero())) {
    return {}
  }

  const subtotalWeights = subtotals.map((subtotal) => subtotal.centsAsDecimal())
  const lineShares = amount.allocate(
    subtotalWeights,
    AllocationStrategies.LargestRemainder
  )

  const shares: Record<string, DistributedCost> = {}
  products.forEach((product, i) => {
    const id = product.id && UUID.fromPB(product.id).toString()
    const quantity = quantities[i]
    if (!id || quantity.isZero()) return
    shares[id] = {
      name,
      unitFee: FractionalMoney.fromMoney(lineShares[i]).div(quantity),
      costAdjustmentId,
    }
  })
  return shares
}
